import { useCallback } from 'react';
import { useToast } from '../../../../context/ToastContext';
import { leaderApi } from '../../../../api/leaderApi';
import { getAssignmentConflicts, sortAssignmentsByVisualOrder } from '../../../../utils/leaderUtils';

/**
 * useLeaderAssignments - Custom Hook zur Verwaltung von Einsatz-Operationen (CRUD).
 * Kapselt Speichern, Löschen und Verschieben von Einsätzen inkl. Konflikt-Checks.
 */
export const useLeaderAssignments = ({
    // State Setters
    setAssignments,
    setAllAssignments,
    setAssignmentsForConflicts,
    setEditStack,
    setForwardStack,
    setEditingAssignment,
    setModalOpen,
    setActiveResources,
    setInteraction,
    setConflictOverviewOpen,
    setNavigationSource,

    // Data / State
    assignmentsForConflicts,
    absencesForConflicts,
    workers,
    vehicles,
    kolonnen,
    isConflictDetectionEnabled,
    editStack,
    mainViewMode,
    navigationSource,
    modalOpen,

    // Other Hook Methods
    pushToHistory,
    resetNavigation,
    loadData,
    loadAllAssignments,
    getDateRange
}) => {
    const { showToast } = useToast();

    /**
     * Synchronisiert geänderte Einsätze mit dem Schatten-State für die Konflikterkennung.
     * Hält nur Daten der letzten 3 Monate vor, um die Performance zu wahren.
     */
    const syncConflictData = useCallback((updatedItems) => {
        const list = Array.isArray(updatedItems) ? updatedItems : [updatedItems];
        const historyDate = new Date();
        historyDate.setMonth(historyDate.getMonth() - 3);
        const historyStartStr = historyDate.toISOString().split('T')[0];
        
        setAssignmentsForConflicts(prev => {
            let next = [...prev];
            list.forEach(item => {
                if (item.start_datetime && item.start_datetime >= historyStartStr) {
                    const exists = next.find(a => String(a.id) === String(item.id));
                    if (exists) {
                        next = next.map(a => String(a.id) === String(item.id) ? item : a);
                    } else {
                        next.push(item);
                    }
                } else {
                    // Falls das Datum entfernt wurde oder zu alt ist, aus Konflikt-State entfernen
                    next = next.filter(a => String(a.id) !== String(item.id));
                }
            });
            return next;
        });
    }, [setAssignmentsForConflicts]);

    /**
     * Prüft, ob durch eine Änderung neue Konflikte entstehen und gibt ggf. ein Alert aus.
     */
    const checkForNewConflicts = useCallback((items) => {
        if (!isConflictDetectionEnabled) return;
        
        const list = Array.isArray(items) ? items : [items];
        const finalMessages = new Set();
        
        list.forEach(item => {
            const conflicts = getAssignmentConflicts(item, assignmentsForConflicts, absencesForConflicts, vehicles);
            if (conflicts.total > 0) {
                Object.keys(conflicts.workers).forEach(wId => {
                    const worker = workers.find(w => String(w.id) === wId);
                    if (!worker) return;
                    
                    const workerMsgs = conflicts.workers[wId];
                    const assignedMember = item.workers?.find(m => String(m.worker_id || m.id) === wId);
                    const kolonne = assignedMember?.kolonne_id ? kolonnen.find(k => String(k.id) === String(assignedMember.kolonne_id)) : null;
                    
                    workerMsgs.forEach(msgText => {
                        let cleanMsg = msgText
                            .replace('Kollision mit Projekt:', 'Doppelbelegung mit')
                            .replace('Doppelbelegung mit Projekt:', 'Doppelbelegung mit');
                        
                        const prefix = (kolonne && cleanMsg.includes('Doppelbelegung')) 
                            ? `Kolonne "${kolonne.internal_name || kolonne.name}"` 
                            : worker.name;
                        
                        finalMessages.add(`${prefix} - ${cleanMsg}`);
                    });
                });

                Object.keys(conflicts.vehicles).forEach(vId => {
                    const vehicle = vehicles.find(v => String(v.id) === vId);
                    if (!vehicle) return;

                    const vehicleMsgs = conflicts.vehicles[vId];
                    const prefix = vehicle.license_plate || vehicle.internal_name;

                    vehicleMsgs.forEach(msgText => {
                        let cleanMsg = msgText
                            .replace('Doppelbelegung mit Projekt:', 'Doppelbelegung mit')
                            .replace('Kollision mit Projekt:', 'Doppelbelegung mit');
                        finalMessages.add(`${prefix} - ${cleanMsg}`);
                    });
                });
            }
        });

        if (finalMessages.size > 0) {
            let alertMsg = '⚠️ Achtung: Durch diese Änderung sind neue Konflikte entstanden:\n\n';
            Array.from(finalMessages).forEach(msg => {
                alertMsg += `• ${msg}\n`;
            });
            showToast(alertMsg, 'warning', 8000); // Longer duration for conflict list
        }
    }, [isConflictDetectionEnabled, assignmentsForConflicts, absencesForConflicts, vehicles, workers, kolonnen]);

    /**
     * Speichert einen Einsatz oder eine Liste von Einsätzen (Batch).
     * Unterstützt optimistische UI-Updates.
     */
    const handleSaveAssignment = useCallback(async (formDataOrArray) => {
        pushToHistory();
        
        // Optimistische UI-Vorschau (nur lokale Listen-Updates)
        const prepareLocalState = (items) => {
            const list = Array.isArray(items) ? items : [items];
            setAssignments(prev => {
                let next = [...prev];
                list.forEach(item => {
                    const exists = next.find(a => a.id === item.id);
                    if (exists) {
                        next = next.map(a => a.id === item.id ? { ...exists, ...item } : a);
                    }
                });
                return next;
            });
        };

        if (Array.isArray(formDataOrArray)) {
            prepareLocalState(formDataOrArray);
        } else if (formDataOrArray.id) {
            prepareLocalState(formDataOrArray);
        }

        try {
            if (Array.isArray(formDataOrArray)) {
                const updates = formDataOrArray.filter(i => i.id);
                const creations = formDataOrArray.filter(i => !i.id);
                
                if (updates.length > 0) {
                    const updatedList = await leaderApi.updateMultipleAssignments(updates);
                    const normalizedUpdatedList = sortAssignmentsByVisualOrder(updatedList);
                    setAssignments(prev => {
                        let nextAssignments = [...prev];
                        normalizedUpdatedList.forEach(updated => {
                            nextAssignments = nextAssignments.map(a => (a.id === updated.id ? updated : a));
                        });
                        return sortAssignmentsByVisualOrder(nextAssignments);
                    });
                    syncConflictData(normalizedUpdatedList);
                }
                
                if (creations.length > 0) {
                    const createdList = await leaderApi.createMultipleAssignments(creations);
                    const normalizedCreatedList = sortAssignmentsByVisualOrder(createdList);
                    setAssignments(prev => sortAssignmentsByVisualOrder([...prev, ...normalizedCreatedList]));
                    syncConflictData(normalizedCreatedList);
                }
                
                checkForNewConflicts(formDataOrArray);
            } else {
                const formData = formDataOrArray;
                if (formData.id) {
                    const updated = await leaderApi.updateAssignment(formData.id, formData);
                    setAssignments(prev => sortAssignmentsByVisualOrder(prev.map(a => String(a.id) === String(formData.id) ? updated : a)));
                    syncConflictData(updated);
                    if (!modalOpen) checkForNewConflicts(formData);
                } else {
                    const created = await leaderApi.createAssignment(formData);
                    setAssignments(prev => sortAssignmentsByVisualOrder([...prev, created]));
                    syncConflictData(created);
                    if (!modalOpen) checkForNewConflicts(formData);
                }
            }

            // Post-Save Navigation / Cleanup
            if (editStack.length > 0) {
                const prev = editStack[editStack.length - 1];
                setEditStack(prevStack => prevStack.slice(0, -1));
                setEditingAssignment(prev);
            } else {
                resetNavigation();
                if (navigationSource === 'conflict-overview') {
                    setConflictOverviewOpen(true);
                }
                setNavigationSource(null);
            }

            if (mainViewMode === 'list') loadAllAssignments();
            loadData(getDateRange());
            showToast('Einsatz erfolgreich gespeichert', 'success');

        } catch (err) {
            console.error('Error saving assignment:', err);
        }
    }, [
        pushToHistory, setAssignments, syncConflictData, modalOpen, checkForNewConflicts, 
        editStack, setEditStack, setEditingAssignment, resetNavigation, 
        navigationSource, setConflictOverviewOpen, setNavigationSource, 
        mainViewMode, loadAllAssignments, loadData, getDateRange
    ]);

    /**
     * Löscht einen Einsatz permanent.
     */
    const handleDeleteAssignment = useCallback(async (id) => {
        pushToHistory();
        try {
            await leaderApi.deleteAssignment(id);
            setAssignments(prev => prev.filter(a => a.id !== id));
            setAssignmentsForConflicts(prev => prev.filter(a => String(a.id) !== String(id)));
            setAllAssignments(prev => prev.filter(a => a.id !== id));
            
            if (editStack.length > 0) {
                const prev = editStack[editStack.length - 1];
                setEditStack(prevStack => prevStack.slice(0, -1));
                setEditingAssignment(prev);
            } else {
                setModalOpen(false);
                setEditingAssignment(null);
                setEditStack([]);
                setForwardStack([]);
                setActiveResources({ workers: [], vehicles: [] });
            }
            
            loadData(getDateRange());
            showToast('Einsatz gelöscht', 'success');
        } catch (err) {
            console.error('Error deleting assignment:', err);
        }
    }, [
        pushToHistory, setAssignments, setAssignmentsForConflicts, setAllAssignments, 
        editStack, setEditStack, setEditingAssignment, setModalOpen, 
        setForwardStack, setActiveResources, loadData, getDateRange
    ]);

    /**
     * Verschiebt einen Einsatz in den undatierten Bereich (Sidebar).
     */
    const handleMoveToUndated = useCallback(async (assignment) => {
        setInteraction(null);
        pushToHistory();
        try {
            const updated = await leaderApi.updateAssignment(assignment.id, {
                status: 'undatiert',
                start_datetime: null,
                end_datetime: null
            });
            
            setAssignments(prev => prev.map(a => String(a.id) === String(updated.id) ? updated : a));
            syncConflictData(updated);
            
            if (mainViewMode === 'list') {
                loadAllAssignments();
            }
            showToast('Einsatz nach "Undatiert" verschoben', 'success');
        } catch (err) {
            console.error('Error moving to undated:', err);
        }
    }, [setInteraction, pushToHistory, setAssignments, syncConflictData, mainViewMode, loadAllAssignments]);

    return {
        handleSaveAssignment,
        handleDeleteAssignment,
        handleMoveToUndated,
        syncConflictData
    };
};
